package ca.northroot.app

import androidx.compose.foundation.layout.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import ca.northroot.app.data.*
import ca.northroot.app.ui.auth.AuthScreen
import ca.northroot.app.ui.dashboard.DashboardScreen
import ca.northroot.app.ui.jobs.JobsScreen
import ca.northroot.app.ui.customers.CustomersScreen
import ca.northroot.app.ui.reports.PayReportsScreen
import ca.northroot.app.ui.map.LiveTeamMapScreen
import ca.northroot.app.ui.theme.NorthrootTheme
import ca.northroot.app.ui.theme.ThemeMode
import kotlinx.coroutines.launch

enum class NavScreen {
    AUTH, DASHBOARD, JOBS, CUSTOMERS, PAY_REPORTS, LIVE_MAP
}

@Composable
fun App() {
    var currentThemeMode by remember { mutableStateOf(ThemeMode.SYSTEM) }
    var currentUserProfile by remember { mutableStateOf<UserProfile?>(null) }
    var currentScreen by remember { mutableStateOf(NavScreen.AUTH) }
    var errorMessage by remember { mutableStateOf<String?>(null) }
    var isInitialized by remember { mutableStateOf(false) }
    
    val scope = rememberCoroutineScope()
    val db = remember { FirebaseDb() }

    // Initialize Firebase
    LaunchedEffect(Unit) {
        try {
            FirebaseConfig.initialize()
            // Check if already logged in
            val user = db.getCurrentUser()
            if (user != null) {
                val profile = db.getProfile(user.uid)
                if (profile != null) {
                    currentUserProfile = profile
                    currentScreen = NavScreen.DASHBOARD
                }
            }
            isInitialized = true
        } catch (e: Exception) {
            errorMessage = "Init Error: ${e.message}"
        }
    }

    NorthrootTheme(themeMode = currentThemeMode) {
        Surface(
            modifier = Modifier.fillMaxSize(),
            color = MaterialTheme.colorScheme.background
        ) {
            if (errorMessage != null) {
                Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                    Column(
                        horizontalAlignment = Alignment.CenterHorizontally,
                        verticalArrangement = Arrangement.Center,
                        modifier = Modifier.padding(32.dp)
                    ) {
                        Text(
                            errorMessage!!,
                            color = MaterialTheme.colorScheme.error,
                            style = MaterialTheme.typography.bodyLarge,
                            textAlign = androidx.compose.ui.text.style.TextAlign.Center
                        )
                        Spacer(Modifier.height(16.dp))
                        Button(onClick = { errorMessage = null }) {
                            Text("Dismiss")
                        }
                    }
                }
                return@Surface
            }

            if (!isInitialized) {
                Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                    Text("Loading Northroot...", color = MaterialTheme.colorScheme.onBackground)
                }
                return@Surface
            }

            when (currentScreen) {
                NavScreen.AUTH -> {
                    AuthScreen(
                        onSignIn = { email, password -> 
                            scope.launch {
                                try {
                                    val user = db.signIn(email, password)
                                    val profile = db.getProfile(user.uid)
                                    if (profile != null) {
                                        currentUserProfile = profile
                                        currentScreen = NavScreen.DASHBOARD
                                    } else {
                                        errorMessage = "Profile not found for this user."
                                    }
                                } catch (e: Exception) {
                                    errorMessage = e.message ?: "Authentication failed."
                                }
                            }
                        },
                        onSignUp = { email, password ->
                            scope.launch {
                                try {
                                    val user = db.signUp(email, password)
                                    var profile = db.getProfile(user.uid)
                                    if (profile == null) {
                                        profile = UserProfile(
                                            uid = user.uid,
                                            email = email,
                                            role = UserRole.USER,
                                            displayName = email.substringBefore("@")
                                        )
                                    }
                                    currentUserProfile = profile
                                    currentScreen = NavScreen.DASHBOARD
                                } catch (e: Exception) {
                                    errorMessage = e.message ?: "Registration failed."
                                }
                            }
                        },
                        themeMode = currentThemeMode,
                        onThemeChange = { currentThemeMode = it }
                    )
                }
                NavScreen.DASHBOARD -> {
                    DashboardScreen(
                        db = db,
                        companyId = currentUserProfile?.companyId ?: "",
                        userProfile = currentUserProfile,
                        themeMode = currentThemeMode,
                        onThemeChange = { currentThemeMode = it },
                        onLogout = { 
                            currentUserProfile = null
                            currentScreen = NavScreen.AUTH
                        },
                        onNavigateToJobs = { currentScreen = NavScreen.JOBS },
                        onNavigateToCustomers = { currentScreen = NavScreen.CUSTOMERS },
                        onNavigateToLiveMap = { currentScreen = NavScreen.LIVE_MAP },
                        onNavigateToPayReports = { currentScreen = NavScreen.PAY_REPORTS }
                    )
                }
                NavScreen.JOBS -> {
                    JobsScreen(
                        db = db,
                        userProfile = currentUserProfile,
                        isAdmin = true,
                        onBack = { currentScreen = NavScreen.DASHBOARD },
                        companyId = currentUserProfile?.companyId ?: "",
                        onLogout = {
                            currentUserProfile = null
                            currentScreen = NavScreen.AUTH
                        }
                    )
                }
                NavScreen.CUSTOMERS -> {
                    CustomersScreen(
                        db = db,
                        isAdmin = true,
                        companyId = currentUserProfile?.companyId ?: "",
                        onBack = { currentScreen = NavScreen.DASHBOARD },
                        onLogout = {
                            currentUserProfile = null
                            currentScreen = NavScreen.AUTH
                        }
                    )
                }
                NavScreen.PAY_REPORTS -> {
                    PayReportsScreen(
                        db = db,
                        userProfile = currentUserProfile,
                        companyId = currentUserProfile?.companyId ?: "",
                        onBack = { currentScreen = NavScreen.DASHBOARD }
                    )
                }
                NavScreen.LIVE_MAP -> {
                    LiveTeamMapScreen(
                        db = db,
                        companyId = currentUserProfile?.companyId ?: "",
                        currentUserId = currentUserProfile?.uid ?: "",
                        onBack = { currentScreen = NavScreen.DASHBOARD }
                    )
                }
            }
        }
    }
}
